/**
 * Sacred Waters — page boot.
 *
 * Owns the visual layer: the WebGL scene behind the WHOLE page, the
 * day→night→dawn arc driven by where you are in the document, the glass
 * cursor, scroll reveals, the FAQ accordion and the sticky buy bar.
 * Product choice, audio and the cart live in sw.js.
 *
 * Nothing here hijacks the scroll. Every section is document flow; the
 * scene simply reads the scroll position the browser already has.
 */
const html = document.documentElement;
const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
const finePointer = matchMedia('(hover: hover) and (pointer: fine)').matches;

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
function smoothstep(e0, e1, x) {
  const t = clamp((x - e0) / (e1 - e0), 0, 1);
  return t * t * (3 - 2 * t);
}

const canvas = document.querySelector('[data-sw-canvas]');
const hero = document.querySelector('[data-sw-hero]');

let assets = {};
try {
  assets = JSON.parse(document.getElementById('sw-scene-assets').textContent);
} catch { assets = {}; }

/* ------------------------------------------------------------------ */
/* The day → night → dawn arc                                         */
/* ------------------------------------------------------------------ */
/**
 * Each section wrapper declares the light it wants with
 * `data-sw-night="0"` or `"1"`. The sky is then a function of scroll
 * position: hold each section's own value through its body, and cross
 * over one viewport-height's worth of scrolling either side of a
 * boundary where the value changes. So the turn of light happens
 * exactly at the seam between two sections, however long the copy in
 * them turns out to be — no invented scroll length, no fixed panels.
 */
function makeBandReader(attr) {
  let bands = [];
  let ro = null;
  const measure = () => {
    bands = [...document.querySelectorAll('[' + attr + ']')].map((el) => {
      const r = el.getBoundingClientRect();
      return {
        top: r.top + scrollY,
        bottom: r.bottom + scrollY,
        value: +el.getAttribute(attr) || 0,
      };
    }).sort((a, b) => a.top - b.top);
  };
  measure();
  addEventListener('resize', measure);
  if ('ResizeObserver' in window) {
    /* kept on the closure: an unreferenced observer is collectable, and
       a stale band map is the difference between the sky turning at the
       seam and turning a screen early */
    ro = new ResizeObserver(measure);
    ro.observe(document.body);
  }

  return function valueAt(y) {
    void ro;
    if (!bands.length) return 0;
    /* the eye judges the sky by the middle of the screen, not the top */
    const focus = y + innerHeight * 0.5;
    const blend = innerHeight * 0.42;
    /* find the band the middle of the screen is in */
    let i = 0;
    while (i + 1 < bands.length && focus >= bands[i + 1].top) i++;
    const b = bands[i];
    let current = b.value;
    /* then cross over whichever edge is close, so the turn lands on the
       seam between two sections rather than snapping on entry */
    const prev = bands[i - 1];
    const next = bands[i + 1];
    if (prev && prev.value !== b.value) {
      const edge = (prev.bottom + b.top) * 0.5;
      if (focus < edge + blend) {
        current = prev.value
          + (b.value - prev.value) * smoothstep(edge - blend, edge + blend, focus);
      }
    }
    if (next && next.value !== b.value) {
      const edge = (b.bottom + next.top) * 0.5;
      if (focus > edge - blend) {
        current = b.value
          + (next.value - b.value) * smoothstep(edge - blend, edge + blend, focus);
      }
    }
    return clamp(current, 0, 1);
  };
}

/* ------------------------------------------------------------------ */
/* Scene                                                              */
/* ------------------------------------------------------------------ */
function webglOk() {
  try {
    const c = document.createElement('canvas');
    return !!(c.getContext('webgl2') || c.getContext('webgl'));
  } catch {
    return false;
  }
}

async function bootScene() {
  if (!canvas || !hero || !assets.engine) return;
  /* The CSS sky painted on .sw-canvas is a complete picture on its own,
     so a device that should not run a fluid simulation simply keeps it:
     reduced motion, no WebGL, or very little memory / very few cores. */
  const thin = (navigator.deviceMemory && navigator.deviceMemory <= 2)
    || (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2);
  if (reduceMotion || thin || !webglOk()) {
    /* the dark chapter still has to be dark without WebGL */
    html.classList.add('sw-static');
    bootStaticNight();
    return;
  }

  let createScene;
  try {
    ({ createScene } = await import(assets.engine));
  } catch (err) {
    console.warn('[sacred-waters] scene failed to load', err);
    html.classList.add('sw-static');
    bootStaticNight();
    return;
  }

  /* Wide enough for the aisle: the crystal is HELD STILL at the centre
     of the screen for the whole dark act and the page scrolls past it,
     so the anchor is a constant and never touches the DOM. */
  const AISLE = 1024;
  const wide = () => innerWidth >= AISLE;
  const FIXED_ANCHOR = { x: 0, y: 0.04, h: 0.52 };

  /* Narrow screens have no room for a lane, so the crystal goes back to
     appearing once, in the hole the ceremony reserves for it — expressed
     the way the scene wants it: clip-space centre plus a height fraction. */
  const anchor = () => {
    if (wide()) return FIXED_ANCHOR;
    const holes = document.querySelectorAll('[data-sw-shard-anchor]');
    if (!holes.length) return null;
    let el = null;
    let best = Infinity;
    const mid = innerHeight * 0.5;
    for (const h of holes) {
      const b = h.getBoundingClientRect();
      if (!b.height) continue;
      const d = Math.abs(b.top + b.height * 0.5 - mid);
      if (d < best) { best = d; el = h; }
    }
    if (!el) return null;
    const r = el.getBoundingClientRect();
    if (!r.height) return null;
    return {
      x: ((r.left + r.width * 0.5) / innerWidth) * 2 - 1,
      y: 1 - ((r.top + r.height * 0.5) / innerHeight) * 2,
      h: clamp(r.height / innerHeight, 0.12, 0.9),
    };
  };

  let scene;
  try {
    scene = createScene(canvas, { urls: assets, anchor });
  } catch (err) {
    console.warn('[sacred-waters] scene failed to start', err);
    html.classList.add('sw-static');
    bootStaticNight();
    return;
  }
  html.classList.add('sw-webgl');

  const nightAt = makeBandReader('data-sw-night');
  const shardAt = makeBandReader('data-sw-shard');
  const shardAtNarrow = makeBandReader('data-sw-shard-mobile');
  let ticking = false;
  let wasNight = false;
  const sync = () => {
    const h = hero.offsetHeight || innerHeight;
    scene.setProgress(clamp(scrollY / h, 0, 1));
    const span = Math.max(1, document.documentElement.scrollHeight - innerHeight);
    scene.setPageProgress(clamp(scrollY / span, 0, 1));
    const n = nightAt(scrollY);
    scene.setNight(n);
    scene.setShard((wide() ? shardAt : shardAtNarrow)(scrollY));
    /* Text cannot be interpolated the way the sky can, so it flips once
       at the midpoint and rides a CSS transition across the seam. */
    const dark = n > 0.5;
    if (dark !== wasNight) {
      wasNight = dark;
      html.classList.toggle('sw-night', dark);
    }
  };
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => { ticking = false; sync(); });
  };
  addEventListener('scroll', onScroll, { passive: true });
  addEventListener('resize', onScroll, { passive: true });
  sync();

  /* Two viewports of warning is enough for the shard's assets to land
     before the sky turns. Without this the dark chapter opens on an
     empty altar on a slow connection. */
  const shardHole = document.querySelector('[data-sw-shard-anchor]');
  if (shardHole && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      io.disconnect();
      scene.preload();
    }, { rootMargin: '200% 0px' });
    io.observe(shardHole);
  } else {
    scene.preload();
  }

  /* A background tab should not be running a fluid simulation. The
     canvas itself is never parked while the tab is visible — the whole
     point is that the ripple follows the pointer on every section. */
  document.addEventListener('visibilitychange', () => {
    scene.setActive(!document.hidden);
  });
}

/* Without WebGL the night sections still need to go dark, so the same
   reader drives the body class on its own. */
function bootStaticNight() {
  const nightAt = makeBandReader('data-sw-night');
  let ticking = false;
  let wasNight = false;
  const sync = () => {
    const dark = nightAt(scrollY) > 0.5;
    if (dark !== wasNight) {
      wasNight = dark;
      html.classList.toggle('sw-night', dark);
    }
  };
  addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => { ticking = false; sync(); });
  }, { passive: true });
  sync();
}

/* ------------------------------------------------------------------ */
/* Glass cursor                                                       */
/* ------------------------------------------------------------------ */
function bootCursor() {
  if (!finePointer || reduceMotion) return;

  const el = document.createElement('div');
  el.className = 'sw-cursor';
  el.setAttribute('aria-hidden', 'true');
  el.innerHTML =
    '<i class="sw-cursor__orb"><svg class="sw-cursor__star" viewBox="0 0 16 16" fill="currentColor">' +
    '<path d="M8 0l1.1 5.3L16 8l-6.9 2.7L8 16l-1.1-5.3L0 8l6.9-2.7z"/></svg></i>';
  document.body.appendChild(el);
  const orb = el.querySelector('.sw-cursor__orb');
  html.classList.add('sw-cursor-on');

  const HOVER = 'a, button, [role="radio"], [role="button"], summary, input, label';
  let mx = -200, my = -200, cx = -200, cy = -200;
  let seen = false;
  let raf = 0;
  let settled = true;

  function loop() {
    raf = 0;
    /* tau-style easing, so the lag feels identical at 60 and 120Hz */
    cx += (mx - cx) * 0.18;
    cy += (my - cy) * 0.18;
    orb.style.transform = 'translate(' + (cx - 24) + 'px,' + (cy - 24) + 'px)';
    /* park when settled: this used to write a transform every frame for
       the whole visit, which is a rAF callback for nothing */
    if (Math.abs(mx - cx) > 0.25 || Math.abs(my - cy) > 0.25) {
      raf = requestAnimationFrame(loop);
      settled = false;
    } else {
      settled = true;
    }
  }
  function wake() {
    if (!raf) raf = requestAnimationFrame(loop);
  }

  addEventListener('pointermove', (e) => {
    if (e.pointerType && e.pointerType !== 'mouse') return;
    mx = e.clientX;
    my = e.clientY;
    if (!seen) {
      seen = true;
      cx = mx;
      cy = my;
      el.classList.add('is-on');
    }
    wake();
  }, { passive: true });

  addEventListener('pointerdown', () => el.classList.add('is-down'), { passive: true });
  addEventListener('pointerup', () => el.classList.remove('is-down'), { passive: true });
  addEventListener('pointerleave', () => el.classList.remove('is-on'), { passive: true });
  document.addEventListener('pointerover', (e) => {
    el.classList.toggle('is-hover', !!(e.target.closest && e.target.closest(HOVER)));
  }, { passive: true });
}

/* ------------------------------------------------------------------ */
/* Reveals                                                            */
/* ------------------------------------------------------------------ */
function bootReveals() {
  const items = document.querySelectorAll('.sw-reveal');
  if (!items.length) return;
  if (reduceMotion || !('IntersectionObserver' in window)) {
    items.forEach((el) => el.classList.add('is-in'));
    return;
  }
  const io = new IntersectionObserver((entries) => {
    for (const e of entries) {
      if (!e.isIntersecting) continue;
      e.target.classList.add('is-in');
      io.unobserve(e.target);
    }
  }, { rootMargin: '0px 0px -12% 0px', threshold: 0.05 });
  items.forEach((el) => io.observe(el));
}

/* ------------------------------------------------------------------ */
/* FAQ                                                                */
/* ------------------------------------------------------------------ */
function bootFaq() {
  for (const btn of document.querySelectorAll('[data-sw-faq-q]')) {
    const panel = document.getElementById(btn.getAttribute('aria-controls'));
    if (!panel) continue;
    btn.addEventListener('click', () => {
      const open = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', open ? 'false' : 'true');
      panel.classList.toggle('is-open', !open);
      /* hidden once closed, so shut answers are out of the tab order
         and out of the accessibility tree, not merely clipped */
      if (open) {
        panel.addEventListener('transitionend', function done(ev) {
          if (ev.propertyName !== 'grid-template-rows') return;
          panel.removeEventListener('transitionend', done);
          if (btn.getAttribute('aria-expanded') === 'false') panel.hidden = true;
        });
        if (reduceMotion) panel.hidden = true;
      } else {
        panel.hidden = false;
      }
    });
  }
}

/* ------------------------------------------------------------------ */
/* Sticky buy bar                                                     */
/* ------------------------------------------------------------------ */
function bootBar() {
  const bar = document.querySelector('[data-sw-bar]');
  if (!bar || !hero || !('IntersectionObserver' in window)) return;
  const buy = document.querySelector('[data-sw-buy]');

  let heroPast = false;
  let atBuy = false;
  const sync = () => html.classList.toggle('sw-bar-on', heroPast && !atBuy);

  new IntersectionObserver(([e]) => { heroPast = !e.isIntersecting; sync(); },
    { threshold: 0 }).observe(hero);
  /* the bar is a shortcut to the closing panel — while that panel is
     itself on screen it is only noise over the real buttons */
  if (buy) {
    new IntersectionObserver(([e]) => { atBuy = e.isIntersecting; sync(); },
      { threshold: 0 }).observe(buy);
  }
}

bootReveals();
bootFaq();
bootBar();
bootCursor();
bootScene();
